import {DigitalAssetVisual} from '@cohort/components-xps/components/DigitalAssetVisual';
import Title from '@cohort/components-xps/components/Title';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import {BaseModal} from '@cohort/wallet/components/modals/BaseModal';
import DrawerModal from '@cohort/wallet/components/modals/DrawerModal';
import {Subtitle} from '@cohort/wallet/components/Subtitle';
import useClickTracker from '@cohort/wallet/hooks/clickTracker';
import useNotify from '@cohort/wallet/hooks/notify';
import useScreenSizeContext from '@cohort/wallet/hooks/useScreenSize';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {getPerkUsageRoute} from '@cohort/wallet/lib/Pages';
import type {AccessesAndOwnership} from '@cohort/wallet/lib/Utils';
import {perkObtentionDate} from '@cohort/wallet/lib/Utils';
import {CaretRight, Check} from '@phosphor-icons/react';
import React, {Fragment, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

interface DigitalAssetCardProps {
  accessesAndOwnership: AccessesAndOwnership;
  onPickNft: (accessesAndOwnership: AccessesAndOwnership) => void;
}

// TODO: remove this and use the new Digital Asset card once it's done
const DigitalAssetCard: React.FC<DigitalAssetCardProps> = ({accessesAndOwnership, onPickNft}) => {
  const {accentColor, hasDarkBg} = useThemeContext();
  const {t} = useTranslation('pages', {keyPrefix: 'space.selectDigitalAssetModal'});
  const ownership = accessesAndOwnership.ownership;
  const perkAccess = accessesAndOwnership.access;
  const {canBeUsed, reachedMaxUsages, usagesLeft} = perkAccess;
  const trackingCallback = useClickTracker('perks.detail.selectDigitalAsset.modal.card', {
    appId: perkAccess.perk.integration?.perkIntegrationId,
    perkId: perkAccess.perk.id,
    digitalAssetId: ownership.digitalAsset.id,
    ownershipId: ownership.id,
    accessId: perkAccess.id,
  });
  const notify = useNotify();

  const handleClick = useCallback(async () => {
    trackingCallback();
    if (!canBeUsed) {
      return;
    }

    if (reachedMaxUsages) {
      notify('error', t('maxUsagesReached'), {autoClose: false});
      return;
    }

    onPickNft(accessesAndOwnership);
  }, [t, notify, canBeUsed, accessesAndOwnership, onPickNft, reachedMaxUsages, trackingCallback]);

  const textColorClass = !canBeUsed
    ? hasDarkBg
      ? 'text-white/25'
      : 'text-black/25'
    : hasDarkBg
      ? 'text-white'
      : 'text-gray-900';

  const secondaryTextColorClass = canBeUsed
    ? hasDarkBg
      ? 'text-white/50'
      : 'text-black/50'
    : hasDarkBg
      ? 'text-white/25'
      : 'text-black/25';

  return (
    <div
      data-testid="nft-picker-card"
      className={classNames(
        'flex w-full rounded-lg p-3',
        hasDarkBg ? 'bg-white/5' : 'bg-black/5',
        !canBeUsed ? 'opcaity-50 cursor-not-allowed' : 'cursor-pointer'
      )}
      onClick={handleClick}
    >
      <div className={classNames('aspect-square h-16 w-16 rounded-lg', !canBeUsed && 'opacity-50')}>
        <DigitalAssetVisual
          digitalAssetImageFileKey={ownership.digitalAsset.imageFileKey}
          animationFileKey={ownership.digitalAsset.animationFileKey}
          disableModal
        />
      </div>
      <div className="my-auto ml-3">
        <div className={classNames('truncate font-medium leading-5', textColorClass)}>
          <div className="flex">
            <div className="xs:max-w-[190px] mr-1 min-w-0 max-w-[230px] truncate break-words sm:max-w-[400px] lg:max-w-[350px] xl:max-w-[550px]">
              {ownership.digitalAsset.collection.title}
            </div>
            <span data-testid="nft-picker-nft-token-id">#{ownership.digitalAsset.tokenId}</span>
          </div>
          <div
            className={classNames('mt-1 text-sm font-normal leading-5', secondaryTextColorClass)}
          >
            <div data-testid="nft-picker-nft-obtention-date">
              {perkObtentionDate(ownership, canBeUsed || perkAccess.raffleStatus === 'lost')}
            </div>
            {perkAccess.raffleStatus !== null
              ? perkAccess.statusMessage
              : usagesLeft !== null &&
                usagesLeft > 0 && <p>{String(t('usagesLeft', {count: usagesLeft}))}</p>}
          </div>
        </div>
      </div>
      <div className="my-auto ml-auto">
        {canBeUsed ? (
          <CaretRight className="my-auto h-6 w-6" style={{color: accentColor}} />
        ) : (
          <Check
            className={classNames('my-auto h-6 w-6', hasDarkBg ? 'text-white/25' : 'text-black/25')}
          />
        )}
      </div>
    </div>
  );
};

type SelectDigitalAssetModalProps = {
  accessesAndOwnerships: AccessesAndOwnership[];
  modalOpened: boolean;
  setModalOpened: (open: boolean) => void;
};

const SelectDigitalAssetModal: React.FC<SelectDigitalAssetModalProps> = ({
  accessesAndOwnerships,
  modalOpened,
  setModalOpened,
}) => {
  const navigate = useNavigate();
  const {isMobile} = useScreenSizeContext();
  const {t} = useTranslation('pages', {keyPrefix: 'space.selectDigitalAssetModal'});
  const {setTransitioning} = useSpaceContext();

  const handlePickNft = (accessesAndOwnership: AccessesAndOwnership): void => {
    setTransitioning({active: true, enterFrom: 'left'});
    navigate(
      getPerkUsageRoute(accessesAndOwnership.ownership.id, accessesAndOwnership.access.perk.id)
    );
  };

  const ModalContent = (
    <div className="flex w-full flex-col space-y-4">
      {accessesAndOwnerships
        .map(accessesAndOwnership => (
          <DigitalAssetCard
            key={accessesAndOwnership.access.id}
            accessesAndOwnership={accessesAndOwnership}
            onPickNft={handlePickNft}
          />
        ))
        .reverse()}
    </div>
  );

  if (accessesAndOwnerships[0] === undefined || modalOpened === false) {
    return <Fragment />;
  }

  return isMobile ? (
    <DrawerModal
      title={<Title content={t('title')} classes="text-2xl font-semibold" />}
      subtitle={<Subtitle content={t('subtitle')} classes="!text-base font-medium" />}
      tracking={{namespace: 'modals.perks.detail.selectDigitalAsset'}}
      onClose={() => setModalOpened(false)}
    >
      {ModalContent}
    </DrawerModal>
  ) : (
    <BaseModal
      title={<Title content={t('title')} classes="text-2xl font-semibold" />}
      subtitle={<Subtitle content={t('subtitle')} classes="!text-base font-medium" />}
      onClose={() => setModalOpened(false)}
      tracking={{namespace: 'modals.perks.detail.selectDigitalAsset'}}
    >
      {ModalContent}
    </BaseModal>
  );
};

export default SelectDigitalAssetModal;
